import PropTypes from 'prop-types';
import React, { useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Random } from '@zedoc/random';
import PatientRecordFilterOption from '../../../../common/models/PatientRecordFilterOption';
import { apiZedocSearchPatientRecordsFilterOptions } from '../../../../common/api/zedoc';
import createSelectors from '../../../../common/selectors/createSelectors';
import defaultFilters from './defaultFilters';
import FiltersToolbar from '../FiltersToolbar';
import store from '../../store';
import { FILTER_CATEGORY__VARIABLE } from '../../../../components/Filters';
import useProjectFilters from '../../../../utils/useProjectFilters';
import Recipient from '../../../../common/models/Recipient';
import Participation from '../../../../common/models/Participation';
import { FILTER_TYPE__ALL_OF } from '../../../../common/constants';

const PatientRecordFilterOptionSelect = createSelectors(
  PatientRecordFilterOption,
  {},
);

// Builds the initial "Enrolment date = today" filter applied to the timeline
// dashboard on first load.
//
// IMPORTANT: this is the WIDGET-state shape, not the API-request shape. The
// widget stores a flat list whose elements are nested ALL_OF groups (see
// `initialFilters` in
// zedoc-provider-web/src/components/Filters/elements/Filters.js). The outer
// `anyOf` wrapper that appears in the final searchPatientRecords selector is
// added later by `cleanFilters(rawFilters, { anyOf: true })` at the fetch
// layer (see DashboardTimeline/ProjectMilestones.js:69). Putting `anyOf`
// here would nest an anyOf inside the widget's implicit top-level anyOf
// and trigger an onChange-cascade infinite render loop in
// components/Filters/elements/Filters.js:106.
//
// We reuse the 'Enrolment date' preset from ./defaultFilters as the inner
// property filter (rather than hand-writing name/type/condition/meta) so the
// widget renders it with the same condition dropdown options as when a user
// picks it from the toolbar. The preset carries `meta.conditions` — without
// that, the Condition dropdown is empty/read-only because the widget has no
// list of allowed conditions to render.
//
// `timezone` comes from the browser; `threshold` is today's local date in
// YYYY-MM-DD.
const buildDefaultInitialFilters = () => {
  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const today = new Intl.DateTimeFormat('en-CA', { timeZone: timezone }).format(
    new Date(),
  );
  const enrolmentPreset = defaultFilters.find(
    (preset) => preset.settings && preset.settings.id === 'createdAt',
  );
  if (!enrolmentPreset) {
    // The 'Enrolment date' preset has been removed/renamed in defaultFilters.
    // Bail without dispatching — the user just sees the empty toolbar like
    // before, which is no worse than the pre-change behaviour.
    return undefined;
  }
  return [
    {
      id: Random.id(),
      type: FILTER_TYPE__ALL_OF,
      settings: {
        filters: [
          {
            ...enrolmentPreset,
            id: Random.id(),
            state: { timezone, threshold: today },
          },
        ],
      },
      meta: { nested: true },
    },
  ];
};

const ConnectedFilters = ({ storeKey, projectId, dashboardId }) => {
  const presets = useProjectFilters({
    projectId,
    defaultFilters,
    defaultCategory: FILTER_CATEGORY__VARIABLE,
    allowedScopes: [Recipient.scopeName, Participation.scopeName],
  });

  // Apply the default Enrolment-date=today filter on the very first load of
  // this dashboard (when the persisted store has never written filters for
  // this storeKey). If the user has previously set OR explicitly cleared
  // filters, the persisted value is non-undefined (an array, possibly empty),
  // and we leave it alone — "this is only for initial call".
  const dispatch = useDispatch();
  const storeKeyFilters = `${storeKey}.filters`;
  const persistedFilters = useSelector(store.get(storeKeyFilters));
  useEffect(() => {
    if (persistedFilters === undefined) {
      const initialFilters = buildDefaultInitialFilters();
      if (initialFilters) {
        dispatch(store.create.set(storeKeyFilters)(initialFilters));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const getOptionsSubscription = useCallback(
    ({
      searchText,
      type,
      condition,
      state,
      settings,
      pageIndex,
      resultsPerPage,
    }) => {
      return apiZedocSearchPatientRecordsFilterOptions.withParams({
        projectId,
        controlId: '$meta.id',
        searchText,
        filter: {
          type,
          condition,
          state,
          settings,
        },
        pageIndex,
        resultsPerPage,
        sorterFilter: {
          label: 1,
        },
      });
    },
    [projectId],
  );

  return (
    <FiltersToolbar
      storeKey={storeKey}
      dashboardId={dashboardId}
      presets={presets}
      optionsSelector={PatientRecordFilterOptionSelect}
      optionsSubscription={getOptionsSubscription}
    />
  );
};

ConnectedFilters.propTypes = {
  storeKey: PropTypes.string.isRequired,
  projectId: PropTypes.string.isRequired,
  dashboardId: PropTypes.string.isRequired,
};

export default ConnectedFilters;
